import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  bulkCreateWorkOrderSalesOrders,
  isPromangApiErrorResponse,
  promangApiErrorsMessage,
  promangApiErrorsMessageFromCaught,
} from "../../../api/work-order";

const STRATEGIES = [
  { id: "per_work_order", labelKey: "work_orders.bulk_create.strategy_per_wo" },
  { id: "by_customer", labelKey: "work_orders.bulk_create.strategy_customer" },
  { id: "by_project", labelKey: "work_orders.bulk_create.strategy_project" },
];

export function WorkOrderBulkCreateOrderDialog({
  open,
  onClose,
  workOrderIds,
  onDone,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [strategy, setStrategy] = useState("per_work_order");
  const [submitting, setSubmitting] = useState(false);

  const count = Array.isArray(workOrderIds) ? workOrderIds.length : 0;

  const handleSubmit = async () => {
    if (!count) return;
    setSubmitting(true);
    try {
      const res = await bulkCreateWorkOrderSalesOrders({
        workOrderIds,
        strategy,
        billingPreparation: {
          include_hours: true,
          include_items: true,
          add_tilleggsarbeider_group: true,
        },
      });
      if (isPromangApiErrorResponse(res)) {
        toast.error(
          promangApiErrorsMessage(res, t("work_orders.bulk_create.failed"))
        );
        return;
      }
      const orders = res?.data?.orders;
      const orderIds = res?.data?.order_ids;
      toast.success(
        t("work_orders.bulk_create.success", { count: orderIds?.length ?? count })
      );
      onDone?.();
      onClose();
      if (Array.isArray(orderIds) && orderIds.length === 1) {
        navigate(`/dashboard/orders/${orderIds[0]}`);
      } else if (Array.isArray(orders) && orders.length === 1 && orders[0]?.id) {
        navigate(`/dashboard/orders/${orders[0].id}`);
      }
    } catch (err) {
      toast.error(
        promangApiErrorsMessageFromCaught(
          err,
          t("work_orders.bulk_create.failed")
        )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-20 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-xl">
              <Dialog.Title className="text-lg font-semibold text-slate-900">
                {t("work_orders.bulk_create.title")}
              </Dialog.Title>
              <p className="mt-2 text-sm text-slate-600">
                {t("work_orders.bulk_create.summary", { count })}
              </p>
              <p className="mt-3 text-xs text-slate-500">
                {t("work_orders.bulk_create.hint")}
              </p>
              <fieldset className="mt-5 space-y-3">
                <legend className="text-sm font-medium text-slate-800">
                  {t("work_orders.bulk_create.strategy_label")}
                </legend>
                {STRATEGIES.map((s) => (
                  <label
                    key={s.id}
                    className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 p-3 hover:bg-slate-50"
                  >
                    <input
                      type="radio"
                      name="bulk-order-strategy"
                      checked={strategy === s.id}
                      onChange={() => setStrategy(s.id)}
                      className="mt-1 h-4 w-4 border-slate-300 text-slate-900 focus:ring-slate-500"
                    />
                    <span className="text-sm text-slate-800">{t(s.labelKey)}</span>
                  </label>
                ))}
              </fieldset>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                  onClick={onClose}
                  disabled={submitting}
                >
                  {t("Close")}
                </button>
                <button
                  type="button"
                  disabled={submitting || !count}
                  className="rounded-xl border border-transparent bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50"
                  onClick={handleSubmit}
                >
                  {submitting
                    ? t("report.sectionDialog.saving")
                    : t("work_orders.bulk_create.submit")}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
